import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { api, fileUrl, money } from "@/lib/api";
import { Swatch } from "@/lib/colors";
import { useStore } from "@/context/StoreContext";
import Hero from "@/components/store/Hero";
import EmptyCatalog from "@/components/store/EmptyCatalog";

const SectionHead = ({ eyebrow, title, link, testid }) => (
  <div className="flex items-end justify-between gap-6 mb-10" data-testid={testid}>
    <div>
      <p className="eyebrow">{eyebrow}</p>
      <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight uppercase text-white mt-3">{title}</h2>
    </div>
    {link && (
      <Link to={link} className="group hidden sm:inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-white transition-colors">
        View all <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
      </Link>
    )}
  </div>
);

const Categories = ({ products }) => {
  const cats = useMemo(() => [...new Set(products.map((p) => p.category).filter(Boolean))].slice(0, 6), [products]);
  if (cats.length < 2) return null;
  return (
    <section className="max-w-7xl 2xl:max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 pt-20" data-testid="categories-section">
      <SectionHead eyebrow="Browse" title="Categories" />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {cats.map((name) => (
          <Link key={name} to={`/shop?category=${encodeURIComponent(name)}`} data-testid={`category-tile-${name.toLowerCase().replace(/\s/g, "-")}`}
            className="group relative aspect-[16/7] overflow-hidden bg-[#0a0a0c] border border-white/10 hover:border-white/30 transition-colors">
            <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between">
              <span className="font-display text-lg sm:text-xl font-semibold uppercase tracking-tight text-white">{name}</span>
              <ArrowUpRight size={18} className="text-white transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

const FeaturedProduct = ({ product }) => {
  const cover = product.cover_photo || product.photos?.[0];
  const soldOut = product.total_stock <= 0;
  const inStock = product.colors?.filter((color) => color.stock > 0) || [];
  const [selectedColor, setSelectedColor] = useState(inStock[0]?.name || product.colors?.[0]?.name || "");
  return (
    <article className="featured-launch fade-up" data-testid={`featured-product-${product.slug}`}>
      <div className="featured-launch-image">
        <p className="featured-launch-index">01 / THE DROP</p>
        {cover && <img src={fileUrl(cover)} alt={product.name} className="featured-launch-photo" />}
        <span className="featured-launch-light" aria-hidden="true" />
      </div>
      <div className="featured-launch-detail">
        {product.category && <p className="eyebrow">{product.category}</p>}
        <div className="featured-launch-title-row">
          <h3 className="font-display text-4xl sm:text-5xl font-bold uppercase text-white" data-testid="featured-product-name">{product.name}</h3>
          <p className="font-mono-ui text-lg text-white">{money(product.price)}</p>
        </div>
        <div className="featured-launch-rule" />
        <p className="text-xs uppercase tracking-[0.16em] text-zinc-500">Select finish</p>
        <div className="featured-launch-colors" data-testid="featured-product-colors">
          {product.colors?.map((color) => (
            <button
              key={color.name}
              type="button"
              disabled={color.stock <= 0}
              onClick={() => setSelectedColor(color.name)}
              className={`featured-launch-color ${selectedColor === color.name ? "is-selected" : ""}`}
              data-testid={`featured-color-${color.name.toLowerCase()}`}
            >
              <Swatch name={color.name} size={12} />
              <span>{color.name}</span>
            </button>
          ))}
        </div>
        {product.description && <p className="featured-launch-copy">{product.description}</p>}
        <Link
          to={`/product/${product.slug}?color=${encodeURIComponent(selectedColor)}`}
          className="featured-launch-button"
          data-testid="featured-product-button"
        >
          View product
          <ArrowRight size={16} />
        </Link>
      </div>
    </article>
  );
};

export default function Home() {
  const { settings } = useStore();
  const [products, setProducts] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, []);

  const featured = useMemo(() => {
    if (!products?.length) return null;
    return products.find((p) => p.id === settings?.homepage_product_id) || products.find((p) => p.featured) || products[0];
  }, [products, settings]);
  const dropTitle = settings?.drop_title || "THE DROP";

  return (
    <div data-testid="home-page">
      <Hero settings={settings} />
      <section className="max-w-7xl 2xl:max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 pt-14 sm:pt-20" data-testid="featured-section">
        <SectionHead eyebrow={settings?.drop_subtitle || "Featured"} title={dropTitle} link={products?.length ? "/shop" : null} />
        {products === null ? (
          <div className="h-64" />
        ) : products.length === 0 ? (
          <EmptyCatalog />
        ) : (
          <FeaturedProduct product={featured} />
        )}
      </section>
      {products && <Categories products={products} />}
    </div>
  );
}
